import React from 'react';
import { Box, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import useUser from '../../hooks/global/useUser';
import { LoadedUser } from '../../interfaces';
import useGetSubscription from '../../hooks/subscriptions/useGetSubscription';
import { ROUTES } from '../../constants/global';
import { isSudoManager, isUserAdminOrManager } from '../../utils/global/helper';
import { canIndependentManageShop } from '../../helpers/businesses';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import { handleSignOut } from '../../helpers/global';

interface DropdownNavMenuProps {
  anchorElUser: null | HTMLElement;
  handleCloseDropdown: () => void;
  setMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Route {
  id: string;
  text: string;
  to: string;
  visible: boolean;
}

function DropdownNavMenu({
  anchorElUser,
  handleCloseDropdown,
  setMobileDrawerOpen,
}: DropdownNavMenuProps) {
  const theme = useTheme();
  const { user } = useUser<LoadedUser>();
  const { data: subData } = useGetSubscription();
  const { selectBusiness } = useUser();

  const hasItemLibrary = useFeatureGuard(Feature.ITEM_LIBRARY);

  const isIndependent = user.business.type === 'INDEPENDENT';
  const isManagerOrIndependent = isUserAdminOrManager(user) || isIndependent;
  const isTeamManager =
    isIndependent || (user.role === 'SHOP_MANAGER' && user.canManageTeam);
  const isPaymentsManager =
    user.business.type === 'STUDIO' && isSudoManager(user);
  const isSubscriptionManager = isIndependent || isSudoManager(user);
  const canManageShop =
    isUserAdminOrManager(user) || canIndependentManageShop(user);
  const isStudioShopManager =
    user.business.type === 'STUDIO' && isUserAdminOrManager(user);

  const routes: Route[] = [
    {
      id: 'shop',
      text: 'Manage Shop',
      to: ROUTES.shop,
      visible: canManageShop,
    },
    {
      id: 'team',
      text: 'Manage Team',
      to: ROUTES.team,
      visible: isTeamManager,
    },
    {
      id: 'subscription',
      text: 'Manage Subscription',
      to: ROUTES.subscription,
      visible: isSubscriptionManager && !!subData?.subscription?.id,
    },
    {
      id: 'bookingLinks',
      text: 'Booking Links',
      to: ROUTES.bookingLinks,
      visible: isManagerOrIndependent,
    },
    {
      id: 'payments',
      text: 'Payments Account',
      to: ROUTES.payments,
      visible: isPaymentsManager,
    },
    {
      id: 'items',
      text: 'Item Library',
      to: ROUTES.items,
      visible: canManageShop && hasItemLibrary,
    },
    {
      id: 'import',
      text: 'Import Data',
      to: ROUTES.import,
      visible: isStudioShopManager,
    },
    {
      id: 'notifications',
      text: 'Notifications',
      to: ROUTES.notifications,
      visible: true,
    },
  ];

  const filteredRoutes = routes.filter(route => route.visible);

  return (
    <Menu
      sx={{ mt: '45px' }}
      id="menu-appbar"
      anchorEl={anchorElUser}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        style: {
          width: 240,
        },
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={Boolean(anchorElUser)}
      onClick={handleCloseDropdown}
      onClose={handleCloseDropdown}>
      <Box>
        <Box paddingX={2} paddingTop={1} paddingBottom={0.5}>
          <Typography
            variant="h4"
            borderBottom={1}
            borderColor={theme.palette.outlineGrey.main}
            color={theme.palette.outlineGrey.main}>
            {user.business.name}
          </Typography>
        </Box>
        {filteredRoutes.map(route => (
          <MenuItem
            key={route.id}
            component={Link}
            sx={{ paddingY: 1 }}
            to={route.to}
            onClick={() => setMobileDrawerOpen(false)}>
            <Typography
              textAlign="center"
              variant="h5"
              color={theme.palette.darkGrey.main}>
              {route.text}
            </Typography>
          </MenuItem>
        ))}
        <MenuItem onClick={handleSignOut}>
          <Typography
            textAlign="center"
            variant="h5"
            color={theme.palette.darkGrey.main}>
            Sign Out
          </Typography>
        </MenuItem>
      </Box>

      {user.allBusinesses.length > 1 && (
        <Box>
          <Box padding={2} paddingBottom={1}>
            <Typography
              variant="h4"
              borderBottom={1}
              borderColor={theme.palette.outlineGrey.main}
              color={theme.palette.outlineGrey.main}>
              Your Locations
            </Typography>
          </Box>
          {user.allBusinesses.map(business => (
            <MenuItem
              key={business.businessId}
              onClick={() => selectBusiness(business.businessId)}>
              <Typography
                textAlign="center"
                variant="h5"
                color={theme.palette.darkGrey.main}>
                {business.name}
              </Typography>
            </MenuItem>
          ))}
        </Box>
      )}
    </Menu>
  );
}

export default DropdownNavMenu;
